import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Venue } from '@gigdriver/domain';
import {
  getOrgVenues,
  createVenue as createVenueApi,
  updateVenue as updateVenueApi,
  deleteVenue as deleteVenueApi,
} from '@gigdriver/client';

export const useVenuesStore = defineStore('venues', () => {
  const venues = ref<Venue[]>([]);
  const isLoading = ref(false);

  // Gigs/calls/positions reference a venue by id — resolve its display name
  // and time zone from the loaded list.
  function byId(id?: string): Venue | undefined {
    return id ? venues.value.find(v => v.id === id) : undefined;
  }
  function nameFor(id?: string): string | undefined {
    return byId(id)?.name;
  }
  function zoneFor(id?: string): string | undefined {
    return byId(id)?.timeZone;
  }
  const error = ref<string | null>(null);

  async function loadOrgVenues(orgId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      venues.value = await getOrgVenues(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load venues';
    } finally {
      isLoading.value = false;
    }
  }

  async function createVenue(orgId: string, data: Omit<Venue, 'id' | 'orgId' | 'createdAt' | 'updatedAt'>) {
    try {
      error.value = null;
      const venueId = await createVenueApi(orgId, data);
      await loadOrgVenues(orgId);
      return venueId;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add venue';
      throw err;
    }
  }

  async function updateVenue(orgId: string, venueId: string, updates: Partial<Venue>) {
    try {
      error.value = null;
      await updateVenueApi(orgId, venueId, updates);
      await loadOrgVenues(orgId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update venue';
      throw err;
    }
  }

  async function deleteVenue(orgId: string, venueId: string) {
    try {
      error.value = null;
      await deleteVenueApi(orgId, venueId);
      venues.value = venues.value.filter(v => v.id !== venueId);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete venue';
      throw err;
    }
  }

  return {
    venues,
    byId,
    nameFor,
    zoneFor,
    isLoading,
    error,
    loadOrgVenues,
    createVenue,
    updateVenue,
    deleteVenue,
  };
});
